import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { cn } from './cn';

/**
 * Props for the Textarea component.
 */
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Label text displayed above the textarea. */
  label?: string;
  /** Error message displayed below the textarea. Sets error styling when present. */
  error?: string;
  /** Helper text displayed below the textarea when no error is present. */
  helperText?: string;
  /** Additional CSS classes for the outer wrapper. */
  className?: string;
}

/**
 * Accessible multi-line text input with label, error state, and helper text.
 * Forwards ref to the underlying textarea element.
 *
 * @param props.label - Label text
 * @param props.error - Error message (also activates error styling)
 * @param props.helperText - Hint shown below the field
 * @example
 * ```tsx
 * <Textarea
 *   label="Description"
 *   helperText="Max 500 characters"
 *   rows={4}
 * />
 * ```
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, error, helperText, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const descriptionId = `${textareaId}-description`;
  const hasError = error != null && error.length > 0;
  const hasDescription = hasError || (helperText != null && helperText.length > 0);

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label != null && (
        <label htmlFor={textareaId} className="text-sm font-medium text-ink">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={textareaId}
        aria-describedby={hasDescription ? descriptionId : undefined}
        aria-invalid={hasError}
        className={cn(
          'w-full rounded-md border bg-card-elevated px-3 py-2 text-sm text-ink placeholder:text-ink-muted',
          'focus:outline-none focus:ring-2 focus:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'resize-y min-h-[80px]',
          hasError
            ? 'border-red-500 focus:ring-red-500'
            : 'border-ink/15 focus:ring-[var(--color-primary,theme(colors.blue.500))]',
        )}
        {...props}
      />
      {hasError && (
        <p id={descriptionId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
      {!hasError && helperText != null && (
        <p id={descriptionId} className="text-xs text-ink-muted">
          {helperText}
        </p>
      )}
    </div>
  );
});
